/**
 * The driver's screen (vehicle's driver link): start / end the duty, GPS status, km, what is still on
 * the phone, and today's places with a ✓ as they are reached. The screen is kept on during a duty
 * (Wake Lock), because most phones stop GPS for a web app when the screen goes off.
 */
import { useEffect, useMemo, useState } from "react";
import { apiErrorText } from "@core/messages";
import type { DriveInfo } from "../field/api";
import { DriveTracker } from "../field/drive";
import { makeId, prefs } from "../platform/browser";
import { backgroundGeo, isNative, openLocationSettings } from "../platform/native";
import { useApp, useEmitter, useSession } from "../ui/context";
import { Banner, Button } from "../ui/components";

type WakeLockLike = { release: () => Promise<void> };

const hm = (iso: string) => new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });

export function DriveScreen() {
  const { app, t, lang } = useApp();
  const { assemblyId } = useSession();
  const native = isNative();
  const tracker = useMemo(
    () =>
      new DriveTracker({
        api: app.api,
        assemblyId: () => app.assemblyId,
        // the APK: GPS through the plugin's foreground service, so it keeps running with the screen locked
        geo: backgroundGeo({ title: t.drvNotifTitle, message: t.drvNotifText }) ?? (typeof navigator !== "undefined" && navigator.geolocation ? navigator.geolocation : null),
        kv: prefs,
        makeId,
        online: () => navigator.onLine !== false,
        onUnauthenticated: (e) => void app.core.expireSession(e as never),
      }),
    [app]
  );
  useEffect(() => () => tracker.dispose(), [tracker]);
  useEmitter(tracker.changes);
  const st = tracker.state;
  const [info, setInfo] = useState<DriveInfo | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const [wake, setWake] = useState(false);

  // the vehicle, the open duty and today's plan; refreshed every 2 minutes. A link just opened / pasted
  // starts the duty by itself (N-PAC, 4 Nov 2026: "लिंक पेस्ट किया, उसका ऑटोमैटिक चलना शुरू हो गया")
  useEffect(() => {
    let off = false;
    const load = () =>
      app
        .online((api, aid) => api.drive(aid))
        .then((d) => {
          if (off) return;
          setInfo(d);
          setErr(null);
          tracker.adopt(d.session);
          if (prefs.get("drive.autostart")) {
            prefs.set("drive.autostart", null);
            if (!d.session || d.session.endedAt) void tracker.start().catch((e) => setErr(apiErrorText(e, lang) ?? t.loginOffline));
          }
        })
        .catch((e) => !off && setErr(apiErrorText(e, lang) ?? t.loginOffline));
    void load();
    const h = setInterval(load, 120_000);
    return () => {
      off = true;
      clearInterval(h);
    };
  }, [app, tracker, lang, t, assemblyId]);

  // a clock for "since" / "ago"
  useEffect(() => {
    const h = setInterval(() => setTick((x) => x + 1), 15_000);
    return () => clearInterval(h);
  }, []);

  // keep the screen on while on duty (a browser only: the APK tracks with the screen off)
  useEffect(() => {
    if (!st.session || native) return;
    let lock: WakeLockLike | null = null;
    const nav = navigator as Navigator & { wakeLock?: { request: (k: "screen") => Promise<WakeLockLike> } };
    const get = () =>
      nav.wakeLock
        ?.request("screen")
        .then((l) => {
          lock = l;
          setWake(true);
        })
        .catch(() => setWake(false));
    void get();
    const onVis = () => document.visibilityState === "visible" && void get();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      void lock?.release().catch(() => undefined);
      setWake(false);
    };
  }, [st.session, native]);

  const start = async () => {
    setBusy(true);
    setErr(null);
    try {
      await tracker.start();
    } catch (e) {
      setErr(apiErrorText(e, lang) ?? t.loginOffline);
    } finally {
      setBusy(false);
    }
  };
  const end = async () => {
    if (!window.confirm(t.drvEndConfirm)) return;
    setBusy(true);
    try {
      await tracker.end();
      const d = await app.online((api, aid) => api.drive(aid));
      setInfo(d);
    } catch (e) {
      setErr(apiErrorText(e, lang) ?? t.loginOffline);
    } finally {
      setBusy(false);
    }
  };

  void tick;
  const s = st.session;
  const minutes = s ? Math.max(0, Math.round((Date.now() - new Date(s.startedAt).getTime()) / 60000)) : 0;
  const fixAgo = st.lastFix ? Math.max(0, Math.round((Date.now() - st.lastFix.at) / 1000)) : null;
  const ago = fixAgo == null ? "" : fixAgo < 60 ? (lang === "hi" ? `${fixAgo} सेकंड पहले` : `${fixAgo}s ago`) : lang === "hi" ? `${Math.round(fixAgo / 60)} मिनट पहले` : `${Math.round(fixAgo / 60)} min ago`;
  const reachedIds = new Set(st.reached.filter((r) => r.target === "place").map((r) => r.id));

  return (
    <div className="stack drive" data-testid="drive">
      {info ? (
        <div className="drv-head">
          <span className="drv-plate">{info.vehicle.regNo}</span>
          {info.vehicle.use === "candidate" ? <span className="chip">{t.drvCandidate}</span> : null}
        </div>
      ) : null}
      {err ? <Banner kind="error">{err}</Banner> : null}

      <section className={`drv-status ${s ? "on" : "off"}`} aria-live="polite" data-testid="drive-status">
        <div className="drv-state">
          <i aria-hidden />
          <b>{s ? t.drvOn : t.drvOff}</b>
          {s ? <span>{t.drvSince(hm(s.startedAt))} · {t.drvMin(minutes)}</span> : null}
        </div>
        {s ? (
          <div className="drv-stats">
            <div>
              <b>{s.km.toFixed(1)}</b>
              <span>{t.drvKm}</span>
            </div>
            <div>
              <b>{s.points}</b>
              <span>{t.drvSent}</span>
            </div>
          </div>
        ) : info ? (
          <p className="drv-today">{t.drvToday(info.today.km, info.today.places, info.today.booths)}</p>
        ) : null}
        {s ? <p className="drv-gps">{st.lastFix ? t.drvGps(st.lastFix.accuracyM, ago) : t.drvWaitingGps}</p> : null}
      </section>

      {s && st.gpsError ? (
        <Banner kind="error" action={native && st.gpsError === "denied" ? { label: t.drvOpenSettings, onClick: openLocationSettings } : undefined}>
          {st.gpsError === "denied" ? (native ? t.drvDeniedApp : t.drvDenied) : t.drvUnavailable}
        </Banner>
      ) : null}
      {st.queued ? <Banner kind="info">{t.drvQueued(st.queued)}</Banner> : null}
      {st.reached.slice(0, 3).map((r) => (
        <Banner key={`${r.target}${r.id}${r.at}`} kind="ok">
          {t.drvJustReached(r.name)}
        </Banner>
      ))}

      {s ? (
        <Button big variant="danger" onClick={end} busy={busy} testId="drive-end">
          {t.drvEnd}
        </Button>
      ) : (
        <Button big onClick={start} busy={busy} disabled={!info} testId="drive-start">
          {t.drvStart}
        </Button>
      )}
      <p className="muted drv-note">{s ? (native ? t.drvBackgroundOk : t.drvKeepOpen) : t.drvPrivacy}</p>
      {s && wake ? <p className="muted drv-note">☀ {t.drvScreenOn}</p> : null}

      {info ? (
        <section className="card">
          <h3 style={{ marginTop: 0 }}>{t.drvPlan}</h3>
          {info.plan ? <p className="muted" style={{ marginTop: -6 }}>{info.plan.area}</p> : null}
          {info.plan?.places.length ? (
            <ol className="drv-plan">
              {info.plan.places.map((p) => {
                const done = p.reached || reachedIds.has(p.id);
                return (
                  <li key={p.id} className={done ? "done" : undefined}>
                    <span aria-hidden>{done ? "✓" : "○"}</span>
                    <b>{p.name}</b>
                    <small>
                      {t.boothNo} {p.boothCode}
                      {done ? ` · ${t.drvReached}` : ""}
                    </small>
                  </li>
                );
              })}
            </ol>
          ) : (
            <p className="muted">{t.drvPlanNone}</p>
          )}
        </section>
      ) : null}

      <section className="card" data-testid="drive-exit-card">
        <p className="muted" style={{ margin: "0 0 8px" }}>
          {t.drvExitHelp}
        </p>
        <Button
          variant="ghost"
          disabled={!!s || busy}
          onClick={() => {
            if (!window.confirm(t.drvExitConfirm)) return;
            prefs.set("drive", null);
            // the link in the address bar would sign the phone in again: drop it
            if (location.search) history.replaceState(null, "", location.pathname);
            void app.leaveDrive();
          }}
          testId="drive-exit"
        >
          ⏏ {t.drvExit}
        </Button>
      </section>
    </div>
  );
}
